import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, updateDoc, doc, deleteDoc, onSnapshot, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { getAuth } from 'firebase/auth';
import 'bootstrap/dist/css/bootstrap.min.css';
import './AffiliationRequestsManager.css';

const AffiliationRequestsManager = () => {
  // États pour les médecins
  const [pendingDoctorRequests, setPendingDoctorRequests] = useState([]);
  const [processedDoctorRequests, setProcessedDoctorRequests] = useState([]);
  const [affiliatedDoctors, setAffiliatedDoctors] = useState([]);
  
  // États pour les patients
  const [affiliationRequests, setAffiliationRequests] = useState([]);
  const [rendezVousRequests, setRendezVousRequests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [medecins, setMedecins] = useState([]);
  
  // États communs
  const [activeTab, setActiveTab] = useState('doctorAffiliations');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('en attente');
  const [filtrePatient, setFiltrePatient] = useState('');
  const [filtreMedecin, setFiltreMedecin] = useState('');
  const [structureData, setStructureData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  
  // État pour détecter si l'écran est mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const auth = getAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) return;
  
      try {
        // Récupérer les informations de la structure
        const structureDoc = await getDoc(doc(db, 'structures', auth.currentUser.uid));
        if (structureDoc.exists()) {
          setStructureData(structureDoc.data());
        }
        
        // Récupérer les médecins de cette structure
        const medecinsQuery = query(
          collection(db, 'medecins'),
          where("structureId", "==", auth.currentUser.uid)
        );
        const medecinsSnapshot = await getDocs(medecinsQuery);
        const medecinsData = medecinsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMedecins(medecinsData);
        setAffiliatedDoctors(medecinsData);
  
        // Mettre en place des écouteurs en temps réel pour les demandes d'affiliation des médecins
        const doctorAffiliationsQuery = query(
          collection(db, 'affiliationRequests'),
          where('structureId', '==', auth.currentUser.uid)
        );
  
        const unsubscribeDoctorAffiliations = onSnapshot(doctorAffiliationsQuery, async (snapshot) => {
          const pending = [];
          const processed = [];
          
          for (const docSnap of snapshot.docs) {
            const requestData = docSnap.data();
            const medecinDoc = await getDoc(doc(db, 'medecins', requestData.medecinId));
            
            if (medecinDoc.exists()) {
              const request = {
                id: docSnap.id,
                ...requestData,
                medecin: medecinDoc.data()
              };
              
              if (requestData.status === 'pending') {
                pending.push(request);
              } else {
                processed.push(request);
              }
            }
          }
          
          setPendingDoctorRequests(pending);
          setProcessedDoctorRequests(processed);
        });
  
        // Mettre en place des écouteurs en temps réel pour les demandes d'affiliation des patients
        const patientAffiliationsQuery = query(
          collection(db, 'affiliations'),
          where('structureId', '==', auth.currentUser.uid)
        );
  
        const unsubscribePatientAffiliations = onSnapshot(patientAffiliationsQuery, async (snapshot) => {
          const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAtFormatted: doc.data().createdAt ? new Date(doc.data().createdAt.toDate()).toLocaleString() : 'Date inconnue'
          }));
          setAffiliationRequests(requests);
          
          // Récupérer les informations des patients
          const patientIds = [...new Set(requests.map(req => req.patientId))];
          if (patientIds.length > 0) {
            const patientsData = [];
            for (const patientId of patientIds) {
              const patientDoc = await getDoc(doc(db, 'patients', patientId));
              if (patientDoc.exists()) {
                patientsData.push({
                  id: patientDoc.id,
                  ...patientDoc.data()
                });
              }
            }
            setPatients(patientsData);
          }
        });
  
        // Mettre en place des écouteurs en temps réel pour les demandes de rendez-vous
        const rendezVousQuery = query(
          collection(db, 'rendezvous'),
          where('structureId', '==', auth.currentUser.uid)
        );
  
        const unsubscribeRendezVous = onSnapshot(rendezVousQuery, (snapshot) => {
          const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAtFormatted: doc.data().createdAt ? new Date(doc.data().createdAt.toDate()).toLocaleString() : 'Date inconnue'
          }));
          setRendezVousRequests(requests);
        });
  
        setLoading(false);
  
        return () => {
          unsubscribeDoctorAffiliations();
          unsubscribePatientAffiliations();
          unsubscribeRendezVous();
        };
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setMessage(`Erreur: ${error.message}`);
        setLoading(false);
      }
    };
  
    fetchData();
  }, [auth.currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Gestion des demandes d'affiliation des médecins
  const handleApproveDoctorRequest = async (request) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, 'affiliationRequests', request.id), {
        status: 'accepted',
        acceptedBy: user.uid,
        acceptedAt: Timestamp.now()
      });
      
      await updateDoc(doc(db, 'medecins', request.medecinId), {
        structureId: user.uid,
        structureAffiliationDate: Timestamp.now(),
        structureNom: request.structureName || '',
        isAffiliated: true
      });
      
      setMessage('Demande d\'affiliation approuvée avec succès.');
      
      const updatedPending = pendingDoctorRequests.filter(req => req.id !== request.id);
      setPendingDoctorRequests(updatedPending);
      
      const updatedRequest = {
        ...request,
        status: 'accepted',
        acceptedBy: user.uid,
        acceptedAt: Timestamp.now()
      };
      setProcessedDoctorRequests([updatedRequest, ...processedDoctorRequests]);
      
      const medecinDoc = await getDoc(doc(db, 'medecins', request.medecinId));
      if (medecinDoc.exists()) {
        const medecinData = medecinDoc.data();
        setAffiliatedDoctors([
          {
            id: request.medecinId,
            ...medecinData,
            structureId: user.uid,
            structureAffiliationDate: Timestamp.now(),
            isAffiliated: true
          },
          ...affiliatedDoctors
        ]);
      }
    } catch (error) {
      console.error('Erreur lors de l\'approbation de la demande:', error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const handleRejectDoctorRequest = async (request) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, 'affiliationRequests', request.id), {
        status: 'rejected',
        rejectedBy: user.uid,
        rejectedAt: Timestamp.now()
      });
      
      setMessage('Demande d\'affiliation rejetée.');
      
      const updatedPending = pendingDoctorRequests.filter(req => req.id !== request.id);
      setPendingDoctorRequests(updatedPending);
      
      const updatedRequest = {
        ...request,
        status: 'rejected',
        rejectedBy: user.uid,
        rejectedAt: Timestamp.now()
      };
      setProcessedDoctorRequests([updatedRequest, ...processedDoctorRequests]);
    } catch (error) {
      console.error('Erreur lors du rejet de la demande:', error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const handleRemoveDoctorAffiliation = async (doctorId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette affiliation ? Le médecin ne sera plus associé à votre structure.')) {
      try {
        const user = auth.currentUser;
        if (!user) return;
        
        await updateDoc(doc(db, 'medecins', doctorId), {
          structureId: null,
          structureAffiliationDate: null,
          structureNom: '',
          isAffiliated: false
        });
        
        const affiliationsQuery = query(
          collection(db, 'affiliationRequests'),
          where("medecinId", "==", doctorId),
          where("structureId", "==", user.uid)
        );
        
        const querySnapshot = await getDocs(affiliationsQuery);
        const deletePromises = [];
        querySnapshot.forEach((doc) => {
          deletePromises.push(deleteDoc(doc.ref));
        });
        
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
        }
        
        await addDoc(collection(db, 'affiliationHistory'), {
          medecinId: doctorId,
          structureId: user.uid,
          action: 'removed',
          timestamp: new Date()
        });
        
        setMessage('Affiliation supprimée avec succès.');
        setAffiliatedDoctors(prev => prev.filter(doc => doc.id !== doctorId));
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'affiliation:', error);
        setMessage(`Erreur: ${error.message}`);
      }
    }
  };

  // Gestion des demandes d'affiliation des patients
  const handleAffiliationRequest = async (id, status) => {
    try {
      await updateDoc(doc(db, 'affiliations', id), {
        statut: status,
        updatedAt: new Date()
      });
      
      if (status === 'acceptée') {
        const affiliationDoc = await getDoc(doc(db, 'affiliations', id));
        if (affiliationDoc.exists()) {
          const affiliationData = affiliationDoc.data();
          const patientId = affiliationData.patientId;
          
          const patientDoc = await getDoc(doc(db, 'patients', patientId));
          if (patientDoc.exists()) {
            await updateDoc(doc(db, 'patients', patientId), {
              structureId: auth.currentUser.uid,
              updatedAt: new Date()
            });
            
            await addDoc(collection(db, 'notifications'), {
              userId: patientId,
              titre: "Demande d'affiliation acceptée",
              message: `Votre demande d'affiliation à ${structureData?.nom || 'notre structure'} a été acceptée.`,
              lu: false,
              createdAt: new Date()
            });
          }
        }
      } else if (status === 'refusée') {
        const affiliationDoc = await getDoc(doc(db, 'affiliations', id));
        if (affiliationDoc.exists()) {
          const affiliationData = affiliationDoc.data();
          const patientId = affiliationData.patientId;
          
          await addDoc(collection(db, 'notifications'), {
            userId: patientId,
            titre: "Demande d'affiliation refusée",
            message: `Votre demande d'affiliation à ${structureData?.nom || 'notre structure'} a été refusée.`,
            lu: false,
            createdAt: new Date()
          });
        }
      }
      
      setMessage(`Demande d'affiliation ${status === 'acceptée' ? 'acceptée' : 'refusée'} avec succès.`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la demande:", error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  // Gestion des rendez-vous
  const handleRendezVousRequest = async (id, status) => {
    try {
      await updateDoc(doc(db, 'rendezvous', id), {
        statut: status,
        updatedAt: new Date()
      });
      
      if (status === 'confirmé') {
        const rendezVousDoc = await getDoc(doc(db, 'rendezvous', id));
        if (rendezVousDoc.exists()) {
          const rendezVousData = rendezVousDoc.data();
          const medecin = medecins.find(m => m.id === rendezVousData.medecinId);
          const patient = patients.find(p => p.id === rendezVousData.patientId);
          
          await addDoc(collection(db, 'rendezvous'), {
            patientId: rendezVousData.patientId,
            medecinId: rendezVousData.medecinId,
            date: rendezVousData.date,
            heure: rendezVousData.heure,
            duree: rendezVousData.duree || 30,
            motif: rendezVousData.motif,
            statut: 'confirmé',
            structureId: auth.currentUser.uid,
            createdAt: new Date(),
            urgent: rendezVousData.urgent || false,
            patientNom: patient ? `${patient.prenom} ${patient.nom}` : rendezVousData.patientNom,
            medecinNom: medecin ? `${medecin.prenom} ${medecin.nom}` : rendezVousData.medecinNom,
            medecinSpecialite: medecin ? medecin.specialite : rendezVousData.medecinSpecialite
          });
          
          await addDoc(collection(db, 'notifications'), {
            userId: rendezVousData.patientId,
            titre: "Rendez-vous confirmé",
            message: `Votre rendez-vous du ${new Date(rendezVousData.date).toLocaleDateString()} à ${rendezVousData.heure} avec Dr. ${medecin?.prenom || ''} ${medecin?.nom || ''} a été confirmé.`,
            lu: false,
            createdAt: new Date()
          });
          
          await addDoc(collection(db, 'notifications'), {
            userId: rendezVousData.medecinId,
            titre: "Nouveau rendez-vous",
            message: `Un rendez-vous avec ${patient?.prenom || ''} ${patient?.nom || ''} a été confirmé pour le ${new Date(rendezVousData.date).toLocaleDateString()} à ${rendezVousData.heure}.`,
            lu: false,
            createdAt: new Date()
          });
        }
      } else if (status === 'annulé') {
        const rendezVousDoc = await getDoc(doc(db, 'rendezvous', id));
        if (rendezVousDoc.exists()) {
          const rendezVousData = rendezVousDoc.data();
          
          await addDoc(collection(db, 'notifications'), {
            userId: rendezVousData.patientId,
            titre: "Rendez-vous annulé",
            message: `Votre demande de rendez-vous pour le ${new Date(rendezVousData.date).toLocaleDateString()} a été annulée par la structure.`,
            lu: false,
            createdAt: new Date()
          });
        }
      }
      
      setMessage(`Demande de rendez-vous ${status === 'confirmé' ? 'acceptée' : 'refusée'} avec succès.`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la demande:", error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  // Fonctions utilitaires
  const getPatientInfo = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient ? patient : { nom: 'Patient inconnu', prenom: '' };
  };

  const getMedecinInfo = (medecinId) => {
    const medecin = medecins.find(m => m.id === medecinId);
    return medecin ? medecin : { nom: 'Médecin inconnu', prenom: '' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'accepted':
      case 'acceptée':
      case 'confirmé':
        return 'bg-success';
      case 'pending':
      case 'en attente':
        return 'bg-warning';
      case 'rejected':
      case 'refusée':
      case 'annulé':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  // Fonction pour afficher les données en format mobile
  const renderMobileDataCard = (data, type) => {
    switch (type) {
      case 'doctorRequest':
        return (
          <div className="mobile-data-card">
            <div className="mobile-data-row">
              <span className="mobile-data-label">Médecin</span>
              <span className="mobile-data-value">{data.medecin?.prenom || 'N/A'} {data.medecin?.nom || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Spécialité</span>
              <span className="mobile-data-value">{data.medecin?.specialite || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Email</span>
              <span className="mobile-data-value">{data.medecin?.email || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Téléphone</span>
              <span className="mobile-data-value">{data.medecin?.telephone || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Date de demande</span>
              <span className="mobile-data-value">{formatDate(data.createdAt)}</span>
            </div>
            <div className="mobile-actions">
              <button 
                className="btn btn-success" 
                onClick={() => handleApproveDoctorRequest(data)}
              >
                <i className="bi bi-check-lg"></i> Accepter
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => handleRejectDoctorRequest(data)}
              >
                <i className="bi bi-x-lg"></i> Refuser
              </button>
            </div>
          </div>
        );

      case 'patientRequest':
        const patient = getPatientInfo(data.patientId);
        return (
          <div className="mobile-data-card">
            <div className="mobile-data-row">
              <span className="mobile-data-label">Patient</span>
              <span className="mobile-data-value">{patient.prenom} {patient.nom}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Email</span>
              <span className="mobile-data-value">{patient.email || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Téléphone</span>
              <span className="mobile-data-value">{patient.telephone || 'N/A'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Date de demande</span>
              <span className="mobile-data-value">{formatDate(data.createdAt)}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Motif</span>
              <span className="mobile-data-value">{data.motif || 'Non spécifié'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Statut</span>
              <span className={`badge ${getStatusBadgeClass(data.statut)}`}>
                {data.statut.charAt(0).toUpperCase() + data.statut.slice(1)}
              </span>
            </div>
            <div className="mobile-actions">
              {data.statut === 'en attente' ? (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => handleAffiliationRequest(data.id, 'acceptée')}
                  >
                    <i className="bi bi-check-lg"></i> Accepter
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => handleAffiliationRequest(data.id, 'refusée')}
                  >
                    <i className="bi bi-x-lg"></i> Refuser
                  </button>
                </>
              ) : (
                <button 
                  className="btn btn-outline-danger" 
                  onClick={() => deleteAffiliationRequest(data.id)}
                >
                  <i className="bi bi-trash"></i> Supprimer
                </button>
              )}
            </div>
          </div>
        );

      case 'rendezVous':
        const rendezVousPatient = getPatientInfo(data.patientId);
        const rendezVousMedecin = getMedecinInfo(data.medecinId);
        return (
          <div className="mobile-data-card">
            <div className="mobile-data-row">
              <span className="mobile-data-label">Patient</span>
              <span className="mobile-data-value">{rendezVousPatient.prenom} {rendezVousPatient.nom}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Médecin</span>
              <span className="mobile-data-value">Dr. {rendezVousMedecin.prenom} {rendezVousMedecin.nom}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Date & Heure</span>
              <span className="mobile-data-value">
                {formatDate(data.date)} à {data.heure}
              </span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Motif</span>
              <span className="mobile-data-value">{data.motif || 'Non spécifié'}</span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Urgent</span>
              <span className="mobile-data-value">
                {data.urgent ? (
                  <span className="badge bg-warning text-dark">Urgent</span>
                ) : (
                  <span className="badge bg-light text-dark">Non</span>
                )}
              </span>
            </div>
            <div className="mobile-data-row">
              <span className="mobile-data-label">Statut</span>
              <span className={`badge ${getStatusBadgeClass(data.statut)}`}>
                {data.statut.charAt(0).toUpperCase() + data.statut.slice(1)}
              </span>
            </div>
            <div className="mobile-actions">
              {data.statut === 'en attente' ? (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => handleRendezVousRequest(data.id, 'confirmé')}
                  >
                    <i className="bi bi-check-lg"></i> Confirmer
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => handleRendezVousRequest(data.id, 'annulé')}
                  >
                    <i className="bi bi-x-lg"></i> Annuler
                  </button>
                </>
              ) : (
                <div className="dropdown">
                  <button 
                    className="btn btn-outline-secondary dropdown-toggle w-100" 
                    type="button" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                  >
                    Changer statut
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleRendezVousRequest(data.id, 'confirmé')}
                      >
                        Confirmé
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleRendezVousRequest(data.id, 'en cours')}
                      >
                        En cours
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleRendezVousRequest(data.id, 'terminé')}
                      >
                        Terminé
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleRendezVousRequest(data.id, 'annulé')}
                      >
                        Annulé
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleRendezVousRequest(data.id, 'absent')}
                      >
                        Patient absent
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="affiliation-manager">
      <div className="affiliation-header">
        <h1 className="affiliation-title">Gestion des demandes</h1>
        {message && (
          <div className={`alert ${message.includes('Erreur') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`} role="alert">
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
          </div>
        )}
      </div>

      <div className="affiliation-tabs">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'doctorAffiliations' ? 'active' : ''}`} 
              onClick={() => setActiveTab('doctorAffiliations')}
            >
              Demandes d'affiliation des médecins
              {pendingDoctorRequests.length > 0 && (
                <span className="badge bg-danger ms-2">
                  {pendingDoctorRequests.length}
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'patientAffiliations' ? 'active' : ''}`} 
              onClick={() => setActiveTab('patientAffiliations')}
            >
              Demandes d'affiliation des patients
              {affiliationRequests.filter(req => req.statut === 'en attente').length > 0 && (
                <span className="badge bg-danger ms-2">
                  {affiliationRequests.filter(req => req.statut === 'en attente').length}
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'rendezVous' ? 'active' : ''}`} 
              onClick={() => setActiveTab('rendezVous')}
            >
              Demandes de rendez-vous
              {rendezVousRequests.filter(req => req.statut === 'en attente').length > 0 && (
                <span className="badge bg-danger ms-2">
                  {rendezVousRequests.filter(req => req.statut === 'en attente').length}
                </span>
              )}
            </button>
          </li>
        </ul>
      </div>  

      {/* Onglet des demandes d'affiliation des médecins */}
      {activeTab === 'doctorAffiliations' && (
        <div>
          <div className="affiliation-card">
            <div className="card-header">
              <h5 className="mb-0">Demandes d'affiliation en attente ({pendingDoctorRequests.length})</h5>
            </div>
            <div className="card-body">
              {pendingDoctorRequests.length > 0 ? (
                isMobile ? (
                  pendingDoctorRequests.map(request => (
                    renderMobileDataCard(request, 'doctorRequest')
                  ))
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Médecin</th>
                          <th>Spécialité</th>
                          <th>Email</th>
                          <th>Téléphone</th>
                          <th>Date de demande</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingDoctorRequests.map(request => (
                          <tr key={request.id}>
                            <td>{request.medecin?.prenom || 'N/A'} {request.medecin?.nom || 'N/A'}</td>
                            <td>{request.medecin?.specialite || 'N/A'}</td>
                            <td>{request.medecin?.email || 'N/A'}</td>
                            <td>{request.medecin?.telephone || 'N/A'}</td>
                            <td>{formatDate(request.createdAt)}</td>
                            <td>
                              <div className="btn-group">
                                <button 
                                  className="btn btn-success"
                                  onClick={() => handleApproveDoctorRequest(request)}
                                >
                                  <i className="bi bi-check-lg"></i> Accepter
                                </button>
                                <button 
                                  className="btn btn-danger"
                                  onClick={() => handleRejectDoctorRequest(request)}
                                >
                                  <i className="bi bi-x-lg"></i> Refuser
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="alert alert-info">
                  Aucune demande d'affiliation en attente.
                </div>
              )}
            </div>
          </div>

          <div className="affiliation-card">
            <div className="card-header">
              <h5 className="mb-0">Médecins affiliés ({affiliatedDoctors.length})</h5>
            </div>
            <div className="card-body">
              {affiliatedDoctors.length > 0 ? (
                isMobile ? (
                  affiliatedDoctors.map(doctor => (
                    <div className="mobile-data-card">
                      <div className="mobile-data-row">
                        <span className="mobile-data-label">Médecin</span>
                        <span className="mobile-data-value">
                          <span className="badge bg-success me-2">Affilié</span>
                          {doctor?.prenom || 'N/A'} {doctor?.nom || 'N/A'}
                        </span>
                      </div>
                      <div className="mobile-data-row">
                        <span className="mobile-data-label">Spécialité</span>
                        <span className="mobile-data-value">{doctor?.specialite || 'N/A'}</span>
                      </div>
                      <div className="mobile-data-row">
                        <span className="mobile-data-label">Email</span>
                        <span className="mobile-data-value">{doctor?.email || 'N/A'}</span>
                      </div>
                      <div className="mobile-data-row">
                        <span className="mobile-data-label">Téléphone</span>
                        <span className="mobile-data-value">{doctor?.telephone || 'N/A'}</span>
                      </div>
                      <div className="mobile-data-row">
                        <span className="mobile-data-label">Date d'affiliation</span>
                        <span className="mobile-data-value">{formatDate(doctor.structureAffiliationDate)}</span>
                      </div>
                      <div className="mobile-actions">
                        <button 
                          className="btn btn-primary" 
                          onClick={() => navigate(`/planning/${doctor.id}`)}
                        >
                          <i className="bi bi-calendar-check"></i> Planning
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleRemoveDoctorAffiliation(doctor.id)}
                        >
                          <i className="bi bi-trash"></i> Supprimer
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Médecin</th>
                          <th>Spécialité</th>
                          <th>Email</th>
                          <th>Téléphone</th>
                          <th>Date d'affiliation</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affiliatedDoctors.map(doctor => (
                          <tr key={doctor.id}>
                            <td>
                              <span className="badge bg-success me-2">Affilié</span>
                              {doctor?.prenom || 'N/A'} {doctor?.nom || 'N/A'}
                            </td>
                            <td>{doctor?.specialite || 'N/A'}</td>
                            <td>{doctor?.email || 'N/A'}</td>
                            <td>{doctor?.telephone || 'N/A'}</td>
                            <td>{formatDate(doctor.structureAffiliationDate)}</td>
                            <td>
                              <div className="btn-group">
                                <button 
                                  className="btn btn-primary"
                                  onClick={() => navigate(`/planning/${doctor.id}`)}
                                >
                                  <i className="bi bi-calendar-check"></i> Planning
                                </button>
                                <button 
                                  className="btn btn-danger"
                                  onClick={() => handleRemoveDoctorAffiliation(doctor.id)}
                                >
                                  <i className="bi bi-trash"></i> Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="alert alert-info">
                  Aucun médecin affilié à votre structure.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onglet des demandes d'affiliation des patients */}
      {activeTab === 'patientAffiliations' && (
        <div>
          <div className="filter-section">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Filtrer par statut</label>
                <select 
                  className="form-select" 
                  value={filtreStatut} 
                  onChange={(e) => setFiltreStatut(e.target.value)}
                >
                  <option value="">Tous les statuts</option>
                  <option value="en attente">En attente</option>
                  <option value="acceptée">Acceptée</option>
                  <option value="refusée">Refusée</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Filtrer par patient</label>
                <select 
                  className="form-select" 
                  value={filtrePatient} 
                  onChange={(e) => setFiltrePatient(e.target.value)}
                >
                  <option value="">Tous les patients</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.prenom} {patient.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="affiliation-card">
            {isMobile ? (
              affiliationRequests
                .filter(request => {
                  if (filtreStatut && request.statut !== filtreStatut) return false;
                  if (filtrePatient && request.patientId !== filtrePatient) return false;
                  return true;
                })
                .map(request => (
                  renderMobileDataCard(request, 'patientRequest')
                ))
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Date de demande</th>
                      <th>Motif</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliationRequests
                      .filter(request => {
                        if (filtreStatut && request.statut !== filtreStatut) return false;
                        if (filtrePatient && request.patientId !== filtrePatient) return false;
                        return true;
                      })
                      .map(request => {
                        const patient = getPatientInfo(request.patientId);
                        return (
                          <tr key={request.id}>
                            <td>
                              <strong>{patient.prenom} {patient.nom}</strong>
                              {patient.email && <div className="small text-muted">{patient.email}</div>}
                              {patient.telephone && <div className="small text-muted">{patient.telephone}</div>}
                            </td>
                            <td>{request.createdAt ? formatDate(request.createdAt.toDate()) : request.createdAtFormatted}</td>
                            <td>{request.motif || 'Non spécifié'}</td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(request.statut)}`}>
                                {request.statut.charAt(0).toUpperCase() + request.statut.slice(1)}
                              </span>
                            </td>
                            <td>
                              {request.statut === 'en attente' ? (
                                <div className="btn-group">
                                  <button 
                                    className="btn btn-success" 
                                    onClick={() => handleAffiliationRequest(request.id, 'acceptée')}
                                  >
                                    <i className="bi bi-check-lg"></i> Accepter
                                  </button>
                                  <button 
                                    className="btn btn-danger" 
                                    onClick={() => handleAffiliationRequest(request.id, 'refusée')}
                                  >
                                    <i className="bi bi-x-lg"></i> Refuser
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="btn btn-outline-danger" 
                                  onClick={() => deleteAffiliationRequest(request.id)}
                                >
                                  <i className="bi bi-trash"></i> Supprimer
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Onglet des demandes de rendez-vous */}
      {activeTab === 'rendezVous' && (
        <div>
          <div className="filter-section">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Filtrer par statut</label>
                <select 
                  className="form-select" 
                  value={filtreStatut} 
                  onChange={(e) => setFiltreStatut(e.target.value)}
                >
                  <option value="">Tous les statuts</option>
                  <option value="en attente">En attente</option>
                  <option value="confirmé">Confirmé</option>
                  <option value="annulé">Annulé</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Filtrer par patient</label>
                <select 
                  className="form-select" 
                  value={filtrePatient} 
                  onChange={(e) => setFiltrePatient(e.target.value)}
                >
                  <option value="">Tous les patients</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.prenom} {patient.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Filtrer par médecin</label>
                <select 
                  className="form-select" 
                  value={filtreMedecin} 
                  onChange={(e) => setFiltreMedecin(e.target.value)}
                >
                  <option value="">Tous les médecins</option>
                  {medecins.map(medecin => (
                    <option key={medecin.id} value={medecin.id}>
                      Dr. {medecin.prenom} {medecin.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="affiliation-card">
            {isMobile ? (
              rendezVousRequests
                .filter(request => {
                  if (filtreStatut && request.statut !== filtreStatut) return false;
                  if (filtrePatient && request.patientId !== filtrePatient) return false;
                  if (filtreMedecin && request.medecinId !== filtreMedecin) return false;
                  return true;
                })
                .map(request => (
                  renderMobileDataCard(request, 'rendezVous')
                ))
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Médecin</th>
                      <th>Date & Heure</th>
                      <th>Motif</th>
                      <th>Urgent</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rendezVousRequests
                      .filter(request => {
                        if (filtreStatut && request.statut !== filtreStatut) return false;
                        if (filtrePatient && request.patientId !== filtrePatient) return false;
                        if (filtreMedecin && request.medecinId !== filtreMedecin) return false;
                        return true;
                      })
                      .map(request => {
                        const patient = getPatientInfo(request.patientId);
                        const medecin = getMedecinInfo(request.medecinId);
                        return (
                          <tr key={request.id} className={request.urgent ? 'table-warning' : ''}>
                            <td>
                              <strong>{patient.prenom} {patient.nom}</strong>
                              {patient.email && <div className="small text-muted">{patient.email}</div>}
                              {patient.telephone && <div className="small text-muted">{patient.telephone}</div>}
                            </td>
                            <td>
                              <strong>Dr. {medecin.prenom} {medecin.nom}</strong>
                              {medecin.specialite && <div className="small text-muted">{medecin.specialite}</div>}
                            </td>
                            <td>
                              <div>{formatDate(request.date)}</div>
                              <strong className="text-primary">{request.heure}</strong>
                            </td>
                            <td>{request.motif || 'Non spécifié'}</td>
                            <td>
                              {request.urgent ? (
                                <span className="badge bg-warning text-dark">Urgent</span>
                              ) : (
                                <span className="badge bg-light text-dark">Non</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(request.statut)}`}>
                                {request.statut.charAt(0).toUpperCase() + request.statut.slice(1)}
                              </span>
                            </td>
                            <td>
                              {request.statut === 'en attente' ? (
                                <div className="btn-group">
                                  <button 
                                    className="btn btn-success" 
                                    onClick={() => handleRendezVousRequest(request.id, 'confirmé')}
                                  >
                                    <i className="bi bi-check-lg"></i> Confirmer
                                  </button>
                                  <button 
                                    className="btn btn-danger" 
                                    onClick={() => handleRendezVousRequest(request.id, 'annulé')}
                                  >
                                    <i className="bi bi-x-lg"></i> Annuler
                                  </button>
                                </div>
                              ) : (
                                <div className="btn-group">
                                  <div className="dropdown">
                                    <button 
                                      className="btn btn-outline-secondary dropdown-toggle" 
                                      type="button" 
                                      data-bs-toggle="dropdown" 
                                      aria-expanded="false"
                                    >
                                      Changer statut
                                    </button>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleRendezVousRequest(request.id, 'confirmé')}
                                        >
                                          Confirmé
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleRendezVousRequest(request.id, 'en cours')}
                                        >
                                          En cours
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleRendezVousRequest(request.id, 'terminé')}
                                        >
                                          Terminé
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleRendezVousRequest(request.id, 'annulé')}
                                        >
                                          Annulé
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleRendezVousRequest(request.id, 'absent')}
                                        >
                                          Patient absent
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                  <button 
                                    className="btn btn-outline-danger" 
                                    onClick={() => deleteRendezVousRequest(request.id)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliationRequestsManager; 
